var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { Arrays, BinarySearch, isAbsent, Lazy, Notifier, NumberComparator, Option, Predicates, Terminable, Terminator } from "@opendaw/lib-std";
import { Browser } from "./browser";
import { Events } from "./events";
import { Key } from "./key";
import { Keyboard } from "./keyboard";
export class ShortcutManager {
    static get() { return new ShortcutManager(); }
    global;
    #contexts = [];
    constructor() {
        this.global = this.createContext(Predicates.alwaysTrue, "Global");
        console.debug("ShortcutManager installed");
    }
    createContext(activation, name, priority) {
        const isActive = typeof activation === "function"
            ? activation :
            () => activation.contains(document.activeElement);
        const context = new ShortcutContext(isActive, name, priority ?? 0);
        const index = BinarySearch.leftMostMapped(this.#contexts, context.priority, (a, b) => b - a, ({ priority }) => priority);
        this.#contexts.splice(index, 0, context);
        console.debug(this.#contexts.map(({ name, priority }) => `${name} (${priority})`).join(", "));
        context.own(Terminable.create(() => Arrays.remove(this.#contexts, context)));
        return context;
    }
    hasConflict(shortcut) {
        return this.#contexts.some(context => context.hasConflict(shortcut));
    }
    handleEvent(event) {
        for (const context of this.#contexts) {
            if (context.active && this.#tryHandle(event, context)) {
                console.debug("consumed by", context.name);
                return;
            }
        }
    }
    #tryHandle(event, context) {
        for (const { shortcut, consume, options } of context.entries) {
            if (!options.activeInTextField && Events.isTextInput(event.target)) {
                continue;
            }
            if (!options.allowRepeat && event.repeat) {
                continue;
            }
            if (!shortcut.matches(event)) {
                continue;
            }
            if (options.preventDefault ?? true) {
                event.preventDefault();
            }
            const returnValue = consume();
            return returnValue !== false; // everything counts as consumed unless one specifically returns false
        }
        return false;
    }
}
__decorate([
    Lazy,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", ShortcutManager)
], ShortcutManager, "get", null);
export class ShortcutContext {
    #terminator = new Terminator();
    #isActive;
    #name;
    #priority;
    #entries = [];
    constructor(isActive, name, priority) {
        this.#isActive = isActive;
        this.#name = name;
        this.#priority = priority;
    }
    get active() { return this.#isActive(); }
    get name() { return this.#name; }
    get priority() { return this.#priority; }
    get entries() { return this.#entries; }
    register(shortcut, consume, options) {
        if (ReservedShortcuts.isReserved(shortcut)) {
            console.warn(`Shortcut ${shortcut.format().join("")} is reserved and cannot be overridden`);
            return Terminable.Empty;
        }
        const entry = { shortcut, consume, options: options ?? ShortcutOptions.Default };
        const index = BinarySearch.leftMostMapped(this.#entries, entry.options.priority ?? 0, NumberComparator, ({ options: { priority } }) => priority ?? 0);
        this.#entries.splice(index, 0, entry);
        return this.#terminator.own({ terminate: () => this.#entries.splice(this.#entries.indexOf(entry), 1) });
    }
    hasConflict(shortcut) { return this.#entries.some(entry => entry.shortcut.equals(shortcut)); }
    own(terminable) { return this.#terminator.own(terminable); }
    terminate() { this.#terminator.terminate(); }
}
export class Shortcut {
    static of(code, modifiers) {
        return new Shortcut(code, modifiers?.ctrl, modifiers?.shift, modifiers?.alt);
    }
    static fromJSON(value) {
        if (typeof value !== "object" || value === null || Array.isArray(value)) {
            return Option.None;
        }
        const { code, ctrl, shift, alt } = value;
        if (typeof code !== "string") {
            return Option.None;
        }
        if (typeof ctrl !== "boolean") {
            return Option.None;
        }
        if (typeof shift !== "boolean") {
            return Option.None;
        }
        if (typeof alt !== "boolean") {
            return Option.None;
        }
        return Option.wrap(new Shortcut(code, ctrl, shift, alt));
    }
    static fromEvent(event) {
        if (Events.isAutofillEvent(event)) {
            return Option.None;
        }
        const code = event.code;
        if (code.startsWith("Shift")
            || code.startsWith("Control")
            || code.startsWith("Alt")
            || code.startsWith("Meta")
            || code === Key.Escape
            || code === Key.Delete
            || code === Key.Backspace
            || code === Key.Enter) {
            return Option.None;
        }
        // For letters, use event.key to get the layout-independent character
        const effectiveCode = code.startsWith("Key")
            ? `Key${event.key.toUpperCase()}`
            : code;
        const shortcut = new Shortcut(effectiveCode, Keyboard.isControlKey(event), event.shiftKey, event.altKey);
        if (ReservedShortcuts.isReserved(shortcut)) {
            return Option.None;
        }
        return Option.wrap(shortcut);
    }
    static #keyNames = {
        [Key.Escape]: ["⎋", "Esc"],
        [Key.Enter]: ["↩", "Enter"],
        [Key.Backspace]: ["⌫", "Backspace"],
        [Key.Delete]: ["⌦", "Del"],
        [Key.DeleteAction]: ["⌫", "Del"],
        [Key.Home]: ["↖", "Home"],
        [Key.End]: ["↘", "End"],
        [Key.PageUp]: ["⇞", "PgUp"],
        [Key.PageDown]: ["⇟", "PgDn"],
        [Key.ArrowUp]: "↑",
        [Key.ArrowDown]: "↓",
        [Key.ArrowLeft]: "←",
        [Key.ArrowRight]: "→",
        [Key.Comma]: ",",
        [Key.Period]: ".",
        [Key.Semicolon]: ";",
        [Key.Quote]: "'",
        [Key.Backquote]: "`",
        [Key.Slash]: "/",
        [Key.Backslash]: "\\",
        [Key.BracketLeft]: "[",
        [Key.BracketRight]: "]",
        [Key.Minus]: "-",
        [Key.Equal]: "="
    };
    static #formatKey(code) {
        if (code.startsWith("Key")) {
            return code.slice(3);
        }
        if (code.startsWith("Digit")) {
            return `#${code.slice(5)}`;
        }
        const mapped = this.#keyNames[code];
        if (isAbsent(mapped)) {
            return code;
        }
        if (typeof mapped === "string") {
            return mapped;
        }
        return Browser.isMacOS() ? mapped[0] : mapped[1];
    }
    #notifier = new Notifier();
    #code;
    #ctrl;
    #shift;
    #alt;
    constructor(code, ctrl = false, shift = false, alt = false) {
        this.#code = code;
        this.#ctrl = ctrl;
        this.#shift = shift;
        this.#alt = alt;
    }
    get code() { return this.#code; }
    get ctrl() { return this.#ctrl; }
    get shift() { return this.#shift; }
    get alt() { return this.#alt; }
    equals(other) {
        return this.#code === other.#code
            && this.#ctrl === other.#ctrl
            && this.#shift === other.#shift
            && this.#alt === other.#alt;
    }
    matches(event) {
        if (Events.isAutofillEvent(event)) {
            return false;
        }
        let codeMatches;
        if (this.#code.startsWith("Key")) {
            // For letters, use event.key to respect keyboard layout (e.g. QWERTZ)
            const expectedLetter = this.#code.slice(3).toLowerCase();
            codeMatches = event.key.toLowerCase() === expectedLetter;
        }
        else {
            codeMatches = event.code === this.#code
                || (this.#code === Key.DeleteAction && Keyboard.isDelete(event));
        }
        return codeMatches
            && this.#ctrl === Keyboard.isControlKey(event)
            && this.#shift === event.shiftKey
            && this.#alt === event.altKey;
    }
    format() {
        const parts = [];
        if (this.#shift) {
            parts.push(Browser.isMacOS() ? "⇧" : "Shift");
        }
        if (this.#alt) {
            parts.push(Browser.isMacOS() ? "⌥" : "Alt");
        }
        if (this.#ctrl) {
            parts.push(Browser.isMacOS() ? "⌘" : "Ctrl");
        }
        parts.push(Shortcut.#formatKey(this.#code));
        if (!Browser.isMacOS()) {
            const result = [];
            for (let i = 0; i < parts.length; i++) {
                if (i > 0) {
                    result.push("+");
                }
                result.push(parts[i]);
            }
            return result;
        }
        return parts;
    }
    overrideWith(shortcut) {
        if (this.#code === shortcut.#code
            && this.#ctrl === shortcut.#ctrl
            && this.#shift === shortcut.#shift
            && this.#alt === shortcut.#alt) {
            return;
        }
        this.#code = shortcut.#code;
        this.#ctrl = shortcut.#ctrl;
        this.#shift = shortcut.#shift;
        this.#alt = shortcut.#alt;
        this.#notifier.notify();
    }
    subscribe(observer) { return this.#notifier.subscribe(observer); }
    toJSON() { return { code: this.#code, ctrl: this.#ctrl, shift: this.#shift, alt: this.#alt }; }
    copy() { return new Shortcut(this.#code, this.#ctrl, this.#shift, this.#alt); }
    toString() { return `{ShortcutKeys ${this.format().join("")}}`; }
}
export var ShortcutOptions;
(function (ShortcutOptions) {
    ShortcutOptions.Default = {
        preventDefault: true,
        allowRepeat: false,
        activeInTextField: false,
        priority: 0
    };
})(ShortcutOptions || (ShortcutOptions = {}));
export var ReservedShortcuts;
(function (ReservedShortcuts) {
    ReservedShortcuts.Copy = Shortcut.of(Key.KeyC, { ctrl: true });
    ReservedShortcuts.Cut = Shortcut.of(Key.KeyX, { ctrl: true });
    ReservedShortcuts.Paste = Shortcut.of(Key.KeyV, { ctrl: true });
    const all = [ReservedShortcuts.Copy, ReservedShortcuts.Cut, ReservedShortcuts.Paste];
    ReservedShortcuts.isReserved = (shortcut) => all.some(reserved => reserved.equals(shortcut));
})(ReservedShortcuts || (ReservedShortcuts = {}));
